import React, { useState } from "react";
import styled from "@emotion/styled";

import {
  Button,
  IconButton as MuiIconButton,
  Card as MuiCard,
  CardActions as MuiCardActions,
  CardContent as MuiCardContent,
  Typography as MuiTypography,
} from "@mui/material";
import { Box, spacing, SpacingProps } from "@mui/system";
import {
  BookmarkBorder as BookmarkOutlinedIcon,
  Bookmark as BookmarkIcon,
} from "@mui/icons-material";

import { setPreUploadedBookmarks } from "../../../redux/slices/bookmark";
import { RootState } from "../../../redux/store";

import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import { getColor } from "../../../utils/clusterColor";
import { ViewFile } from "../../../utils/fnsRedux";
import { Bookmark } from "../../../types/app";
import ConfidenceChip from "./../ConfidenceChip";

const Card = styled(MuiCard)<{
  width: number | undefined;
  backgroundcolor: string;
}>`
  height: 100%;
  width: ${(props) => props.width}px;
  border-radius: 15px;
  box-shadow: 0px 0px 5px -1px rgba(0, 0, 0, 0.4);
  background-color: ${(props) => props.backgroundcolor};
`;

const CardContent = styled(MuiCardContent)`
  border-bottom: 1px solid ${(props) => props.theme.palette.grey[300]};
  height: calc(100% - ${45}px);
  padding: 10px 20px 20px 20px;
`;

const CardActions = styled(MuiCardActions)`
  border-top: 1px solid black;
`;

const TopSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TextHeading = styled(MuiTypography)`
  font-weight: bold;
  font-size: 15px;
`;

const IconButton = styled(MuiIconButton)`
  &:hover {
    background-color: transparent;
  }
`;

interface TypographyProps extends SpacingProps {
  component?: string;
}
const Typography = styled(MuiTypography)<TypographyProps>(spacing);

type ProjectProps = {
  width?: number;
  rank: number;
  description: string[];
  summary: string;
  answer: string;
  pdfName: string;
  page: number;
  score: number;
  cluster: number;
};

function QueryResultCard(result: ProjectProps) {
  const searchState = useAppSelector((state: RootState) => state.search);
  const bookmarkState = useAppSelector((state: RootState) => state.bookmark);
  const [hover, setHover] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const onClick = ViewFile();
  const actualText = result.description
    .slice(
      Math.max(0, result.description.length / 2),
      result.description.length / 2 + 9
    )
    .join(" ");
  const querySelected = bookmarkState.preUploadedBookmarks.some(
    (bookmark) => bookmark.displayText === result.description.join("")
  );

  function onBookmarkClick() {
    let preSelectedBookmarks = [...bookmarkState.preUploadedBookmarks];
    if (querySelected) {
      const index = preSelectedBookmarks.findIndex(
        (bookmark) => bookmark.displayText === result.description.join("")
      );
      preSelectedBookmarks = preSelectedBookmarks.filter(
        (bookmark, idx) => idx !== index
      );
    } else {
      preSelectedBookmarks.push({
        index: bookmarkState.bookmarks.length,
        pdfName: result.pdfName,
        page: result.page,
        title: searchState.searchQuery,
        description: "",
        answer: result.answer,
        group: "",
        displayText: result.description.join(""),
        searchText: result.description,
      } as Bookmark);
    }
    dispatch(setPreUploadedBookmarks(preSelectedBookmarks));
  }

  return (
    <Card
      width={result.width}
      backgroundcolor={getColor(
        searchState.clusters.length,
        result.cluster,
        true
      )}
    >
      <CardContent>
        <TopSection>
          <Typography gutterBottom variant="h5" component="h2">
            Result {result.rank + 1}
          </Typography>
          <IconButton
            onClick={() => onBookmarkClick()}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
          >
            {hover || querySelected ? (
              <BookmarkIcon />
            ) : (
              <BookmarkOutlinedIcon />
            )}
          </IconButton>
        </TopSection>

        <ConfidenceChip score={result.score} />
        {result.answer !== "" && (
          <Box>
            <TextHeading color="primary" sx={{ marginTop: "10px" }}>
              Answer
            </TextHeading>
            <Typography mb={4}>{result.answer}</Typography>
          </Box>
        )}
        {result.summary !== "" && (
          <Box>
            <TextHeading color="primary"> Summary </TextHeading>
            <Typography mb={4}>{result.summary}</Typography>
          </Box>
        )}
        <TextHeading color="primary">Actual Text </TextHeading>
        <Typography mb={4} component="p">
          {actualText}
        </Typography>
      </CardContent>
      <CardActions>
        <Button
          size="small"
          color="primary"
          onClick={() =>
            onClick(result.description, result.pdfName, result.page)
          }
        >
          View
        </Button>
        <Typography
          marginLeft={3}
          maxWidth={`calc(100% - ${130}px)`}
          noWrap
          component="div"
          textOverflow="ellipsis"
        >
          {result.pdfName}
        </Typography>
        <Typography color="textSecondary" component="div">
          page {result.page}
        </Typography>
      </CardActions>
    </Card>
  );
}

export default QueryResultCard;
